"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  TOPUP_CONFIG,
  TOPUP_SUGGESTED_AMOUNTS,
  creditsFromUSD,
} from "@/lib/utils";
import { TopupAPI } from "@/lib/api";
import { toast } from "sonner";
import { FillAppError } from "@/lib/FillAppError";
import posthog from "posthog-js";
interface TopupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TopupDialog({ open, onOpenChange }: TopupDialogProps) {
  const [usdAmount, setUsdAmount] = useState<number>(50);
  const [isEditMode, setIsEditMode] = useState(false);
  const [inputValue, setInputValue] = useState<string>("50");
  const [validationError, setValidationError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  const credits = creditsFromUSD(usdAmount);

  // Auto-resize input based on content
  useEffect(() => {
    if (spanRef.current && inputRef.current && isEditMode) {
      const spanWidth = spanRef.current.offsetWidth;
      // Use actual width for better accuracy, minimum 30px for single digits
      inputRef.current.style.width = `${Math.max(spanWidth + 4, 30)}px`;
    }
  }, [inputValue, isEditMode]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditMode]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setUsdAmount(50);
      setIsEditMode(false);
      setInputValue("50");
      setValidationError("");
      setIsLoading(false);
    }
  }, [open]);

  const validateAmount = (amount: number): boolean => {
    if (
      amount < TOPUP_CONFIG.MIN_USD_AMOUNT ||
      amount > TOPUP_CONFIG.MAX_USD_AMOUNT
    ) {
      setValidationError(
        `Amount must be between $${
          TOPUP_CONFIG.MIN_USD_AMOUNT
        } and $${TOPUP_CONFIG.MAX_USD_AMOUNT.toLocaleString()}`
      );
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleAmountClick = () => {
    if (!isLoading) {
      setIsEditMode(true);
      setInputValue(usdAmount.toString());
      setValidationError("");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Clear validation error while typing
    if (validationError) {
      setValidationError("");
    }
  };

  const handleInputBlur = () => {
    const numValue = parseFloat(inputValue);

    setUsdAmount(numValue);

    setIsEditMode(false);

    validateAmount(numValue);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
    }
  };

  const handlePresetClick = (amount: number) => {
    if (!isLoading) {
      setUsdAmount(amount);
      setInputValue(amount.toString());
      setIsEditMode(false);
      setValidationError("");
    }
  };

  const handleCustomClick = () => {
    if (!isLoading) {
      setIsEditMode(true);
      setInputValue(usdAmount.toString());
      setValidationError("");
    }
  };

  const handleContinue = async () => {
    if (!validateAmount(usdAmount)) {
      return;
    }

    setIsLoading(true);

    try {
      // Track purchase initiation
      posthog.capture("topup_purchase_initiated", {
        usdAmount,
        credits,
      });

      // Create checkout session
      const result = await TopupAPI.createTopupCheckout(usdAmount);

      // Redirect to Stripe
      window.location.href = result.checkoutUrl;
    } catch (error) {
      setIsLoading(false);

      // Track failure
      posthog.capture("topup_purchase_failed", {
        usdAmount,
        credits,
        error: error instanceof FillAppError ? error.errorCode : "unknown",
      });

      // Show error toast
      const errorMessage =
        error instanceof FillAppError
          ? error.message
          : "Failed to create checkout session. Please try again.";
      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Buy credits</DialogTitle>
          <DialogDescription>
            Enter the amount you&apos;d like to spend.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* USD Amount Display/Input - Centered and Larger */}
          <div className="space-y-3 flex flex-col items-center">
            {isEditMode ? (
              <div className="flex items-center justify-center h-[60px]">
                <span className="text-5xl font-bold leading-none">$</span>
                <div className="relative">
                  {/* Hidden span to measure width */}
                  <span
                    ref={spanRef}
                    className="text-5xl font-bold leading-none invisible absolute whitespace-pre"
                    aria-hidden="true"
                  >
                    {inputValue || "0"}
                  </span>
                  {/* Actual input */}
                  <input
                    ref={inputRef}
                    type="number"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyDown}
                    className="text-5xl font-bold leading-none h-[60px] py-0 px-0 m-0 border-0 outline-none focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    disabled={isLoading}
                  />
                </div>
              </div>
            ) : (
              <button
                onClick={handleAmountClick}
                disabled={isLoading}
                className="text-5xl font-bold leading-none h-[60px] flex items-center hover:opacity-70 transition-opacity cursor-pointer"
              >
                <AnimatePresence mode="wait">
                  <motion.span
                    key={usdAmount}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    $
                    {usdAmount.toLocaleString("en-US", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  </motion.span>
                </AnimatePresence>
              </button>
            )}

            {/* Credit Calculation */}
            <div className="text-muted-foreground overflow-hidden h-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={credits}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.16 }}
                  className="flex items-center gap-1 text-base"
                >
                  <span>=</span>
                  <span className="font-medium">
                    {credits.toLocaleString()} credits
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Preset Amount Buttons */}
          <div className="flex flex-wrap gap-2">
            {TOPUP_SUGGESTED_AMOUNTS.map((amount) => (
              <Button
                key={amount}
                variant={usdAmount === amount ? "default" : "outline"}
                size="sm"
                onClick={() => handlePresetClick(amount)}
                disabled={isLoading}
                className="flex-shrink-0"
              >
                ${amount}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCustomClick}
              disabled={isLoading}
              className="flex-shrink-0"
            >
              Custom
            </Button>
          </div>

          {/* Disclaimer */}
          <Alert className="border-muted bg-muted/50">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Credits are valid for one year from purchase date.
            </AlertDescription>
          </Alert>

          {/* Validation Error */}
          <div className="min-h-[24px]">
            <AnimatePresence mode="wait">
              {validationError && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-sm text-destructive"
                >
                  {validationError}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleContinue} disabled={isLoading || !!validationError}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Continue to Payment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
